import { axiosInstance } from "./axios";
import { queryOptions } from "@tanstack/react-query";
import { logger } from "@/lib/logger";

export interface TournamentResponse {
    id: number;
    group: string;
    name: string;
    tournamentType: string;
    year?: number;
    originId?: number;
    matches: MatchResponse[];
}

export interface PlayerResponse {
    id: number;
    firstName?: string;
    lastName: string;
}

export interface MatchResponse {
    id: number;
    date?: string;
    status?: string;
    homeGoals?: number;
    awayGoals?: number;
    shotsAgainstHome: number;
    shotsAgainstAway: number;
    awayTeam: TeamResponse;
    homeTeam: TeamResponse;
}

export interface TeamResponse {
    id: number;
    fullName: string;
    shortName: string;
}
const VALERENGA_ID = 527810;

export const entitiesApi = {
    // Get all tournaments
    getTournaments: async (): Promise<TournamentResponse[]> => {
        const response = await axiosInstance.get<TournamentResponse[]>(`/${VALERENGA_ID}/tournaments`);
        return response.data;
    },

    // Get matches for a specific tournament
    getTournamentMatches: async (tournamentId: number): Promise<MatchResponse[]> => {
        const response = await axiosInstance.get<MatchResponse[]>(`/${tournamentId}/${VALERENGA_ID}/matches`);
        return response.data;
    },

    // Get players for a specific tournament and team
    getTournamentTeamPlayers: async (tournamentId: number, teamId: number): Promise<PlayerResponse[]> => {
        const response = await axiosInstance.get<PlayerResponse[]>(`/${tournamentId}/${teamId}/players`);
        return response.data;
    },

    // Get teams for a specific tournament
    getTournamentTeams: async (tournamentId: number): Promise<TeamResponse[]> => {
        const response = await axiosInstance.get<TeamResponse[]>(`/${tournamentId}/teams`);
        return response.data;
    },

    // Get all players from Valerenga team in a tournament (the system only shows Valerenga data)
    getAllTournamentPlayers: async (tournamentId: number): Promise<PlayerResponse[]> => {
        try {
            // The system is designed for Valerenga team only (ID: 527810)
            const valerengaTeamId = 527810;
            const players = await entitiesApi.getTournamentTeamPlayers(tournamentId, valerengaTeamId);
            return players;
        } catch (error) {
            logger.error("Failed to fetch Valerenga players:", error);
            return [];
        }
    },
};

export const entityTournamentsQueryOptions = queryOptions({
    queryKey: ["entity-tournaments"],
    queryFn: () => entitiesApi.getTournaments(),
    staleTime: 5 * 60 * 1000,
});

export const entityTournamentMatchesQueryOptions = (tournamentId: number) =>
    queryOptions({
        queryKey: ["entity-tournament-matches", tournamentId],
        queryFn: () => entitiesApi.getTournamentMatches(tournamentId),
        staleTime: 5 * 60 * 1000,
    });

export const entityTournamentPlayersQueryOptions = (tournamentId: number) =>
    queryOptions({
        queryKey: ["entity-tournament-players", tournamentId],
        queryFn: () => entitiesApi.getAllTournamentPlayers(tournamentId),
        staleTime: 5 * 60 * 1000,
    });
